import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Pagination,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from './PageHeader';
import {
  getAccessControlUsers,
  updateUserNotificationsEnabled,
  updateUserDashboardWsStatus,
  forceLogoutUser,
  forceLogoutEveryone,
  getAllUsernames,
} from '../utils/api';

const PRIMARY = '#40518A';
const ROWS_PER_PAGE = 10;

const getRoleLabel = (user) => {
  if (user.is_super_admin) return 'Super Admin';
  if (user.role === 'admin') return 'Admin';
  return 'User';
};

const getRoleStyles = (user) => {
  if (user.is_super_admin) return { bgcolor: '#fce7f3', color: '#9d174d' };
  if (user.role === 'admin') return { bgcolor: '#e0e7ff', color: '#3730a3' };
  return { bgcolor: '#e5e7eb', color: '#374151' };
};

const AccessControl = () => {
  const { user: currentUser, isSuperAdmin } = useAuth();

  const [users, setUsers] = useState([]);
  const [allUsernames, setAllUsernames] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, mode: null, user: null });
  const [logoutEveryoneLoading, setLogoutEveryoneLoading] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadUsers = useCallback(async (currentPage = page, currentSearch = search) => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * ROWS_PER_PAGE;
      const data = await getAccessControlUsers(ROWS_PER_PAGE, offset, currentSearch, true);
      if (data?.users) {
        setUsers(data.users);
        setTotal(data.total || 0);
      }
    } catch {
      showSnackbar('Failed to load users.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  const loadUsernames = useCallback(async (q = '') => {
    try {
      const data = await getAllUsernames(q, true);
      setAllUsernames(Array.isArray(data?.names) ? data.names : []);
    } catch {
      setAllUsernames([]);
    }
  }, []);

  useEffect(() => {
    loadUsers(page, search);
  }, [page]);

  useEffect(() => {
    loadUsernames();
  }, []);

  const handlePageChange = (_, newPage) => setPage(newPage);

  const setTogglingFor = (userId, field, value) => {
    setToggling((prev) => ({ ...prev, [`${userId}_${field}`]: value }));
  };

  const handleNotificationToggle = async (user) => {
    const newValue = !user.notifications_enabled;
    setTogglingFor(user.user_id, 'notif', true);
    try {
      await updateUserNotificationsEnabled(user.user_id, newValue);
      setUsers((prev) =>
        prev.map((u) => (u.user_id === user.user_id ? { ...u, notifications_enabled: newValue } : u))
      );
      showSnackbar(`Notifications ${newValue ? 'enabled' : 'disabled'} for ${user.username}.`);
    } catch {
      showSnackbar(`Failed to update notifications for ${user.username}.`, 'error');
    } finally {
      setTogglingFor(user.user_id, 'notif', false);
    }
  };

  const handleDashboardWsToggle = async (user) => {
    const newValue = !user.dashboard_ws_enabled;
    setTogglingFor(user.user_id, 'ws', true);
    try {
      await updateUserDashboardWsStatus(user.user_id, newValue);
      setUsers((prev) =>
        prev.map((u) => (u.user_id === user.user_id ? { ...u, dashboard_ws_enabled: newValue } : u))
      );
      showSnackbar(`Dashboard WebSocket ${newValue ? 'enabled' : 'disabled'} for ${user.username}.`);
    } catch {
      showSnackbar(`Failed to update Dashboard WebSocket for ${user.username}.`, 'error');
    } finally {
      setTogglingFor(user.user_id, 'ws', false);
    }
  };

  const openForceLogoutConfirm = (user) => {
    setConfirmDialog({ open: true, mode: 'single', user });
  };

  const openLogoutEveryoneConfirm = () => {
    setConfirmDialog({ open: true, mode: 'everyone', user: null });
  };

  const closeForceLogoutConfirm = () => {
    setConfirmDialog({ open: false, mode: null, user: null });
  };

  const confirmForceLogout = async () => {
    const mode = confirmDialog.mode;
    const user = confirmDialog.user;
    closeForceLogoutConfirm();

    if (mode === 'everyone') {
      setLogoutEveryoneLoading(true);
      try {
        const res = await forceLogoutEveryone();
        if (res.ok || res.status === 202) {
          let msg = 'All the users will be signed out shortly.';
          try {
            const data = await res.json();
            if (data?.message) msg = data.message;
          } catch { /* ignore */ }
          showSnackbar(msg);
        } else {
          let msg = 'Failed to start logout everyone.';
          try {
            const data = await res.json();
            if (data?.detail) msg = data.detail;
          } catch { /* ignore */ }
          showSnackbar(msg, 'error');
        }
      } catch {
        showSnackbar('Failed to start logout everyone.', 'error');
      } finally {
        setLogoutEveryoneLoading(false);
      }
      return;
    }

    if (!user) return;
    setTogglingFor(user.user_id, 'logout', true);
    try {
      const res = await forceLogoutUser(user.user_id);
      if (res.ok) {
        showSnackbar(`${user.username} has been logged out.`);
      } else {
        let msg = 'Failed to log out user.';
        try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch { /* ignore */ }
        showSnackbar(msg, 'error');
      }
    } catch {
      showSnackbar(`Failed to log out ${user.username}.`, 'error');
    } finally {
      setTogglingFor(user.user_id, 'logout', false);
    }
  };

  const isCurrentUser = (uid) => currentUser?.user_id === uid || currentUser?.id === uid;

  const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
  };

  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Access denied. Super admin privileges required.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Container maxWidth="lg">
        <PageHeader
          title="Access Control"
          subtitle="Manage user notifications, live dashboard, and logout settings."
        />

        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
          {/* Header bar */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              User Access Settings
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', flex: 1, alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Autocomplete
                freeSolo
                size="small"
                options={allUsernames}
                value={search || null}
                inputValue={search || ''}
                onInputChange={(_, newValue, reason) => {
                  setSearch(newValue || '');
                  if (reason === 'reset') return;
                }}
                onChange={(_, newValue) => {
                  const q = typeof newValue === 'string' ? newValue : '';
                  setSearch(q);
                  setPage(1);
                  loadUsers(1, q);
                }}
                sx={{ minWidth: { xs: '100%', sm: 220 }, width: { xs: '100%', sm: 'auto' } }}
                renderInput={(params) => (
                  <Tooltip
                    title={search || ''}
                    arrow
                    placement="bottom"
                    PopperProps={{ disablePortal: true }}
                    disableHoverListener={!search}
                  >
                    <TextField
                      {...params}
                      placeholder="Search by Name"
                      variant="outlined"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const q = e.currentTarget.value || '';
                          setSearch(q);
                          setPage(1);
                          loadUsers(1, q);
                        }
                      }}
                    />
                  </Tooltip>
                )}
              />

              <Button
                variant="outlined"
                color="error"
                startIcon={
                  logoutEveryoneLoading
                    ? <CircularProgress size={18} color="inherit" />
                    : <LogoutIcon />
                }
                onClick={openLogoutEveryoneConfirm}
                disabled={logoutEveryoneLoading}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Logout Everyone
              </Button>
            </Box>
          </Box>

          {/* Loading bar */}
          {loading ? <LinearProgress /> : null}

          {/* Table */}
          {!loading && users.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No active users found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Notifications</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Live Dashboard</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Logout</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => {
                    const roleStyles = getRoleStyles(user);
                    const notifToggling = toggling[`${user.user_id}_notif`];
                    const wsToggling = toggling[`${user.user_id}_ws`];
                    const logoutToggling = toggling[`${user.user_id}_logout`];
                    const isSelf = isCurrentUser(user.user_id);

                    return (
                      <TableRow key={user.user_id} hover>
                        {/* Username + email */}
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontWeight: 600 }}>{user.username}</Typography>
                            {user.email && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                  fontSize: 11,
                                }}
                              >
                                {user.email}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>

                        {/* Role */}
                        <TableCell>
                          <Chip
                            size="small"
                            label={getRoleLabel(user)}
                            sx={{ ...roleStyles, fontWeight: 600 }}
                          />
                        </TableCell>

                        {/* Notifications toggle */}
                        <TableCell align="center">
                          {notifToggling ? (
                            <CircularProgress size={20} sx={{ color: PRIMARY }} />
                          ) : (
                            <Tooltip
                              title={user.notifications_enabled ? 'Disable notifications' : 'Enable notifications'}
                              arrow
                            >
                              <Switch
                                size="small"
                                checked={Boolean(user.notifications_enabled)}
                                onChange={() => handleNotificationToggle(user)}
                                sx={switchSx}
                              />
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* Dashboard WS toggle — super admin can toggle for anyone including themselves */}
                        <TableCell align="center">
                          {wsToggling ? (
                            <CircularProgress size={20} sx={{ color: PRIMARY }} />
                          ) : (
                            <Tooltip
                              title={user.dashboard_ws_enabled ? 'Disable dashboard WebSocket' : 'Enable dashboard WebSocket'}
                              arrow
                            >
                              <Switch
                                size="small"
                                checked={Boolean(user.dashboard_ws_enabled)}
                                onChange={() => handleDashboardWsToggle(user)}
                                sx={switchSx}
                              />
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* Force Logout — hidden for the logged-in super admin's own row */}
                        <TableCell align="center">
                          {isSelf ? null : logoutToggling ? (
                            <CircularProgress size={20} sx={{ color: '#b91c1c' }} />
                          ) : (
                            <Tooltip title="Force logout" arrow>
                              <IconButton
                                size="small"
                                onClick={() => openForceLogoutConfirm(user)}
                                sx={{
                                  color: '#b91c1c',
                                  '&:hover': { bgcolor: '#fee2e2' },
                                  border: '1px solid',
                                  borderColor: '#fca5a5',
                                  borderRadius: 1,
                                  p: 0.5,
                                }}
                              >
                                <LogoutIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size="small"
                sx={{ '& .MuiPaginationItem-root.Mui-selected': { bgcolor: PRIMARY, color: '#fff' } }}
              />
            </Box>
          )}
        </Card>
      </Container>

      {/* Force Logout Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeForceLogoutConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirmDialog.mode === 'everyone' ? 'Logout Everyone' : 'Logout User'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.mode === 'everyone' ? (
            <>
              <Typography>
                Are you sure you want to logout <strong>all other users</strong>?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Everyone except you will be signed out. You will still be logged in.
              </Typography>
            </>
          ) : (
            <>
              <Typography>
                Are you sure you want to logout{' '}
                <strong>{confirmDialog.user?.username}</strong> from all active sessions?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                The user will be immediately signed out and will need to log in again.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForceLogoutConfirm} variant="outlined">Cancel</Button>
          <Button
            onClick={confirmForceLogout}
            variant="contained"
            color="error"
            startIcon={<LogoutIcon />}
          >
            {confirmDialog.mode === 'everyone' ? 'Logout Everyone' : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccessControl;
